import { formatMoney, formatMoneyParts, isDrawnCurrency } from '@diora/contracts';
import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';

import { useSettings } from '@/hooks/use-settings';
import { useLocale } from '@/providers/locale';

/**
 * A price, with the currency drawn when it has no glyph to type.
 *
 * Saudi Arabia's 2025 riyal symbol is in no shipping font, so `Intl` can only
 * write "SAR" or "ر.س." and the mark has to be painted — see `.dio-riyal` in
 * the brand layer. Where it goes is still `Intl`'s call, which is why the split
 * lives in `@diora/contracts` and not here: the editor and the guest have to
 * see the same price in the same shape.
 *
 * The mark carries the currency's name, or a price reads to a screen reader as
 * a bare number.
 */
export function Money({ amount, fallback }: { amount: string | null; fallback?: ReactNode }) {
  const { t } = useTranslation();
  const { locale } = useLocale();
  const { currency } = useSettings();

  if (!isDrawnCurrency(currency)) {
    return <>{formatMoney(amount, currency, locale) ?? fallback ?? null}</>;
  }

  const parts = formatMoneyParts(amount, currency, locale);
  if (!parts) return <>{fallback ?? null}</>;

  return (
    <>
      {parts.map((part, index) =>
        part.type === 'currency' ? (
          <i key={index} className="dio-riyal" role="img" aria-label={t('common.currencyName')} />
        ) : (
          <span key={index}>{part.value}</span>
        ),
      )}
    </>
  );
}
